import { PedigreeListDto } from '@common/dto/pedigree/list-dto';
import * as React from 'react';
import * as pedigreeService from '../services/pedigrees';
import { ColumnListDto } from '@common/dto/column/list-dto';
import { NewTableLoadMetadata, NewTableSortingDirection } from '@common/components/new-table';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { getModuleFolders } from '@common/actions/folders';
import { I18n } from 'react-redux-i18n';
import { getFolderFields } from '@common/actions/formats';
import { FieldListDto } from '@common/dto/field/list-dto';
import { generateId } from '@common/utils/common';
import {number} from "prop-types";

export const usePedigreesController = (props: { getColumnId: (col: ColumnListDto) => string }) => {
  const { getColumnId } = props;
  const pedigreeItemUniqProperty = 'customPedigreeItemUniqProperty';

  // A named constant is clearer than a "magic number" in the code.
  const API_COLUMN_INDEX_OFFSET = 1;

  const [pedigreesLoadMetadata, setPedigreesLoadMetadata] = React.useState<NewTableLoadMetadata>({
    pagination: {
      size: 50,
      start: 0,
    },
  });
  const [pedigreesCount, setPedigreesCount] = React.useState(0);
  const [pedigrees, setPedigrees] = React.useState<Array<PedigreeListDto>>([]);
  const [pedigreesLoading, setPedigreesLoading] = React.useState(false);
  const [pedigreesColumns, setPedigreesColumns] = React.useState<Array<ColumnListDto>>([]);
  const [pedigreesSelectedItems, setPedigreesSelectedItems] = React.useState<Array<PedigreeListDto>>([]);
  const [pedigreesSearchKeyword, setPedigreesSearchKeyword] = React.useState('');

  const handleGetPedigrees = async (params: NewTableLoadMetadata & { columns: Array<ColumnListDto>; folderId?: FolderPrimaryListDto['id']; loadedPedigrees: Array<PedigreeListDto>; searchKeyword?: string }) => {
    try {
      setPedigreesLoading(true);

      const { filtering, pagination, sorting } = params;

      const columns = params.columns.map((x): ColumnListDto => {
        const isSorted = getColumnId(x) === sorting?.id;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : false,
        };
      });
  
      const filters = filtering?.columns || {};
      const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
        const columnIndex = params.columns.findIndex((x) => getColumnId(x) == columnId);
        const value = filters[columnId];
  
        return {
          columnId: columnIndex + API_COLUMN_INDEX_OFFSET,
          operator1: value?.operator1 ?? null,
          operator2: value?.operator2 ?? null,
          value1: value?.whereClause1 ?? null,
          value2: value?.whereClause2 ?? null,
          andOr: value?.andOR,
        };
      });

      const { data, totalCount } = await pedigreeService.getPedigrees({
        pagination,
        filtering: {
          search: params.searchKeyword,
          filterApplied: filtering?.filter ?? false,
          columnFilters: columnFiltersProcessed,
          columns,
          folderId: params.folderId,
        },
      });

      const processedData = data.map((x) => {
        (x as Record<string, any>)[pedigreeItemUniqProperty] = generateId();

        return x;
      });

      const finalData = pagination.start > 0
        ? [...params.loadedPedigrees, ...processedData]
        : processedData;

      setPedigrees(finalData);
      setPedigreesCount((x) => pagination.start > 0 ? x : totalCount);
      setPedigreesLoadMetadata({
        pagination,
        sorting,
        filtering
      });
      setPedigreesSearchKeyword(params.searchKeyword || '');
    } finally {
      setPedigreesLoading(false);
    }
  };

  const updatePedigreesColumns = React.useCallback(async (columns: Array<ColumnListDto>) => {
    await pedigreeService.savePedigreesColumns(columns);
    setPedigreesColumns(columns);
  }, []);

  const getAndSetPedigreesColumns = React.useCallback(async () => {
    const cols = await pedigreeService.getPedigreesColumns();
    setPedigreesColumns(cols);

    return cols;
  }, []);
  
  const getFoldersForPedigreeColumn = React.useCallback(() => {
    const pedigreesId = 6;

    return getModuleFolders({ folderTypeId: pedigreesId, setUp: { rootFolderImgUrl: 'gif/df_pedigree.gif', rootFolderName: I18n.t('Pedigree Data Folders') } });
  }, []);

  const getModuleFolderFieldsForPedigreeColumn = React.useCallback((params: { searchQuery?: string; folderId?: FolderPrimaryListDto['id']; system: boolean }) => {
    const pedigreesContext = 2;
    
    return getFolderFields({
      searchQuery: params.searchQuery,
      folderId: params.folderId,
      contextId: pedigreesContext,
      includeTables: false,
    }).then((fields) => fields.map((field) => ({
      fieldId: field.id,
      fieldName: field.name,
      fieldContext: field.context,
      fieldTypeId: field.type,
      systemField: field.system,
      imageURL: field.imageURL,
      fullPath: field.fullPath,
      options: field.options,
    })) as unknown as FieldListDto[]);
  }, []);

  const restorePedigreesColumnsToDefault = React.useCallback(async () => {
    const cols = await pedigreeService.restorePedigreesColumnsToDefault();
    setPedigreesColumns(cols);
    return cols;
  }, []);

  const renamePedigreeItem = React.useCallback((pedigreeId: string, name: string) => {
    setPedigrees(currentPedigrees =>
        currentPedigrees.map(pedigree => {
          if (pedigree.id === pedigreeId) {
            return { ...pedigree, name: name };
          }
          return pedigree;
        })
    );
  }, []);


  const auditPedigreeView = React.useCallback(async (pedigreeId: PedigreeListDto['id']) => {
    await pedigreeService.callAuditPedigreeView(pedigreeId);
  }, []);

  return {
    restorePedigreesColumnsToDefault,
    pedigreesSearchKeyword,
    pedigreeItemUniqProperty,
    getModuleFolderFieldsForPedigreeColumn,
    getFoldersForPedigreeColumn,
    pedigreesLoadMetadata,
    pedigrees,
    pedigreesLoading,
    pedigreesCount,
    handleGetPedigrees,
    pedigreesSelectedItems,
    pedigreesColumns,
    getAndSetPedigreesColumns,
    updatePedigreesColumns,
    setPedigreesSelectedItems,
    renamePedigreeItem,
    auditPedigreeView,
  };
};